import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, CheckCircle } from 'lucide-react';

export const AboutPage = () => {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="hero-section py-16 md:py-20">
        <div className="container-law">
          <div className="breadcrumb text-white/60 mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span>About</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white">About the Firm</h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20">
        <div className="container-law">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-3xl text-navy mb-6">Inside Counsel, Delivered Through an Outside Counsel Model</h2>
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  Erlichman Law supports the companies that power modern drug development and life sciences innovation. Our clients build the software platforms, data assets, and specialized services that pharmaceutical and biotech companies depend on to develop, test, and commercialize new therapies.
                </p>
                <p>
                  We do not treat these companies like generic software vendors. We take the time to understand how their platforms function, how data is generated and processed, how services are delivered, and how customers actually use the product in clinical, regulatory, or commercial workflows.
                </p>
                <p>
                  That understanding allows us to negotiate complex SaaS, data, and services agreements that reflect the realities of the business. We routinely handle agreements involving global deployments, regulated data, cross-border transfers, validation requirements, inspection readiness, and long-term commercial relationships with large pharmaceutical and biotech customers.
                </p>
                <p>
                  We act as an extension of your internal legal and business teams. We know the recurring issues, the risk tolerances, the internal decision makers, and the business rationale behind each position. Over time, that institutional knowledge reduces friction, shortens deal cycles, and produces consistent outcomes across transactions.
                </p>
              </div>

              <div className="divider my-10"></div>

              <h3 className="font-heading text-2xl text-navy mb-6">What Guides Our Work</h3>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-green/10 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading text-green font-bold">01</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-navy mb-1">Deep Product Understanding</h4>
                    <p className="text-gray-600 text-sm">We invest upfront to understand your platforms, how data flows, how services are delivered, and how customers use your products.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-green/10 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading text-green font-bold">02</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-navy mb-1">Baseline Positions and Playbooks</h4>
                    <p className="text-gray-600 text-sm">We help you develop agreement terms and negotiation playbooks that align with your products, services, and regulatory posture.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-green/10 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading text-green font-bold">03</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-navy mb-1">Institutional Knowledge</h4>
                    <p className="text-gray-600 text-sm">We build knowledge over time that reduces friction, shortens deal cycles, and produces consistent outcomes.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 space-y-6">
                <div className="bg-gray-50 border border-gray-200 p-6">
                  <h4 className="font-heading text-lg text-navy mb-4">Our Clients Include</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-sm text-gray-600">Drug development software</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-sm text-gray-600">Clinical trial technology</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-sm text-gray-600">Real-world data platforms</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-sm text-gray-600">Biosimulation tools</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-sm text-gray-600">Regulatory software</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-sm text-gray-600">Life sciences consulting</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-navy p-6 text-white">
                  <h4 className="font-heading text-lg mb-4">Contact Us</h4>
                  <p className="text-white/70 text-sm mb-4">Ready to discuss how we can support your commercial and technology matters?</p>
                  <Link to="/contact" className="btn-green w-full justify-center text-sm">
                    Get in Touch
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
